"use client"

import { Head, router, usePage } from "@inertiajs/react"
import { ArrowLeft } from "@untitledui/icons"
import { useState } from "react"

import { batyrGroupPhotoBlob as fallbackImage } from "@/assets/images/photos"
import { SplitBackgroundEffect, SplitImageWrapper } from "@/components/auth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Link } from "@/components/ui/link"
import { BatyrLogo, BatyrLogoVariant } from "@/components/ui/logo-batyr"
import { AuthSplitLayout } from "@/layouts/auth/auth-split-layout"
import { identityPasswordResetPath, signInPath } from "@/routes"

interface PageProps {
  errors: Record<string, string>
  [key: string]: unknown
}

export default function ForgotPassword() {
  const { errors } = usePage<PageProps>().props
  const [processing, setProcessing] = useState(false)

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    router.post(
      identityPasswordResetPath(),
      Object.fromEntries(new FormData(e.currentTarget)),
      {
        onStart: () => setProcessing(true),
        onFinish: () => setProcessing(false),
      },
    )
  }

  return (
    <AuthSplitLayout sidebar={<SplitImageWrapper src={fallbackImage} />}>
      <SplitBackgroundEffect />
      <Head title="Forgot password" />
      <header className="flex flex-col items-center gap-6 text-center">
        <BatyrLogo variant={BatyrLogoVariant.ICON} className="h-12 w-auto" />
        <div className="flex flex-col gap-2 md:gap-3">
          <h1 className="text-display-sm text-primary font-semibold text-balance">
            Forgot password?
          </h1>
          <p className="text-md text-tertiary">
            No worries, we&apos;ll send you reset instructions.
          </p>
        </div>
      </header>

      <form onSubmit={submit} className="flex flex-col gap-6">
        <div className="flex flex-col gap-5">
          <Input
            label="Email"
            type="email"
            name="email"
            placeholder="you@example.com"
            size="md"
            isRequired
            hideRequiredIndicator
            isInvalid={!!errors?.email}
            errorMessage={errors?.email}
            autoFocus
            autoComplete="email"
          />
        </div>

        <div className="flex flex-col gap-4">
          <Button
            type="submit"
            size="lg"
            isDisabled={processing}
            isLoading={processing}
            showTextWhileLoading
          >
            Reset password
          </Button>
        </div>
      </form>

      <div className="flex justify-center gap-1 text-center">
        <Link
          href={signInPath()}
          className="text-tertiary hover:text-secondary flex items-center gap-2 text-sm font-semibold"
        >
          <ArrowLeft className="size-4" />
          Back to log in
        </Link>
      </div>
    </AuthSplitLayout>
  )
}
